import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Switch,
    TextField,
    Button,
    Paper,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ITemplateWorksheetProps, Template, IWorksheet } from '../../types/types';
import { FieldGroup } from '../../types/enums';
import { normalizeCustomName } from '../../utilities/utilities';

// Types
export interface TemplateWorksheetRef {
    isDirty: boolean;
    getData: () => Template | null;
    reset: () => void;
}

interface TemplateWorksheetsProps {
    templateWorksheetProps: ITemplateWorksheetProps;
}

const TemplateWorksheets = forwardRef<TemplateWorksheetRef, TemplateWorksheetsProps>(
    ({ templateWorksheetProps }, ref) => {
        const onDirtyChange: Function = templateWorksheetProps.onDirtyChange;
        const handleOpenNextDialog: Function = templateWorksheetProps.handleOpenNextDialog;

        const [template, setTemplate] = useState<Template | null>(templateWorksheetProps.incomingTemplate);
        const lastSavedTemplate = (JSON.parse(JSON.stringify(templateWorksheetProps.lastSavedTemplate)));
        const [selectedWorksheet, setSelectedWorksheet] = useState<IWorksheet | null>(null);

        // Compute isDirty by comparing current state with original
        const isDirty = React.useMemo(() => {

            if (!template || !lastSavedTemplate) return false;

            return JSON.stringify(template) !== JSON.stringify(lastSavedTemplate);
        }, [template, lastSavedTemplate]);

        // Notify parent component when dirty state changes
        useEffect(() => {
            onDirtyChange?.(isDirty);
        }, [isDirty, onDirtyChange]);

        // Handle worksheet toggle
        const handleToggleWorksheet = useCallback(
            (id: number) => {
                if (!template) return;

                setTemplate((prev) => {
                    if (!prev) return prev;
                    const worksheets = prev.worksheets?.map((worksheet) =>
                        worksheet.id === id
                            ? {
                                ...worksheet,
                                isEnabled: !worksheet.isEnabled,
                                customName: null
                            }
                            : worksheet
                    ) ?? null;
                    return {
                        ...prev,
                        worksheets,
                    };
                });
            },
            [template]
        );

        // Handle custom name change
        const handleCustomNameChange = useCallback(
            (id: number, value: string) => {
                if (!template) return;

                const normalizedValue = normalizeCustomName(value);

                setTemplate((prev) => {
                    if (!prev) return prev;
                    const worksheets = prev.worksheets?.map((worksheet) =>
                        worksheet.id === id
                            ? { ...worksheet, customName: normalizedValue } : worksheet
                    ) ?? null;
                    return {
                        ...prev,
                        worksheets,
                    };
                });
            },
            [template]
        );

        // Open edit modal
        const handleEditClick = useCallback((worksheet: IWorksheet) => {
            setSelectedWorksheet(worksheet);
            handleOpenNextDialog(worksheet, template);
        }, [handleOpenNextDialog, template]);


        // Reset changes
        const resetChanges = useCallback(() => {

            if (lastSavedTemplate) {
                setTemplate(JSON.parse(JSON.stringify(lastSavedTemplate))); // Deep copy
            }

        }, [lastSavedTemplate]);

        function isWorksheetEnabled(value: boolean, id: number): boolean {
            // if worksheet(id) is required, it should be enabled
            const worksheet = template?.worksheets?.find((worksheet) => worksheet.id === id);

            if (worksheet && worksheet.isRequired) {
                return true;
            }

            return value;
        }

        function isWorksheetDisabled(id: number): boolean {
            // if worksheet(id) is required, it should be disabled
            const worksheet = template?.worksheets?.find((worksheet) => worksheet.id === id);

            if (worksheet && worksheet.isRequired) {
                return true;	// disabled
            }

            return false;	// enabled
        }

        function isCustomNameDisabled(id: number): boolean {
            const worksheet = template?.worksheets?.find((worksheet) => worksheet.id === id);
            return !worksheet?.isEnabled;
        }

        function isSheetEditable(id: number): boolean {
            const worksheet = template?.worksheets?.find((worksheet) => worksheet.id === id);
            return worksheet?.fieldGroup !== FieldGroup.Instruction;
        }

        // DataGrid columns
        const columns: GridColDef[] = [
            {
                field: 'isEnabled',
                headerName: '',
                width: 70,
                sortable: false,
                renderCell: (params: GridRenderCellParams<IWorksheet>) => (
                    <Switch
                        checked={isWorksheetEnabled(params.row.isEnabled, params.row.id)}
                        disabled={isWorksheetDisabled(params.row.id)}
                        onChange={() => handleToggleWorksheet(params.row.id)}
                        color="primary"
                    />
                ),
            },
            {
                field: 'codeName',
                headerName: 'Name',
                flex: 1,
                minWidth: 150,
                sortable: false,
            },
            {
                field: 'customName',
                headerName: 'Custom Name',
                flex: 1,
                minWidth: 200,
                sortable: false,
                renderCell: (params: GridRenderCellParams<IWorksheet>) => (
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={params.value || ''}
                        onChange={(e) =>
                            handleCustomNameChange(params.row.id, e.target.value)
                        }
                        onKeyDown={(e) => {
                            // Prevent DataGrid from capturing these keyboard events
                            if (
                                e.key === ' ' || // Space
                                (e.ctrlKey && e.key === 'a') || // Ctrl+A
                                (e.ctrlKey && e.key === 'c') || // Ctrl+C
                                (e.ctrlKey && e.key === 'v') || // Ctrl+V
                                (e.ctrlKey && e.key === 'x')    // Ctrl+X
                            ) {
                                e.stopPropagation();
                            }
                        }}
                        placeholder={params.row.codeName}
                        disabled={isCustomNameDisabled(params.row.id)}
                        sx={{
                            '& .MuiInputBase-input::placeholder': {
                                color: 'grey.500',
                                fontStyle: 'italic',
                                fontWeight: 300,
                                opacity: 1,
                            },
                        }}
                    />
                ),
            },
            {
                field: 'options',
                headerName: 'Options',
                width: 120,
                sortable: false,
                renderCell: (params: GridRenderCellParams<IWorksheet>) => (
                        isSheetEditable(params.row.id) &&
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleEditClick(params.row)}
                            >
                                Edit
                            </Button >
                ),
            },
        ];

        // Expose methods via ref
        useImperativeHandle(ref, () => ({
            isDirty,
            getData: () => template,
            reset: resetChanges,
        }), [isDirty, template, resetChanges]);

        if (!template) {
            return <Typography>No template found</Typography>;
        }

        return (
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Template Worksheets
                </Typography>

                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    variant="standard"
                    label="Description"
                    value={template.description || ''}
                    onChange={(e) => setTemplate(prev => prev ? { ...prev, description: e.target.value } : prev)}
                    InputLabelProps={{
                        shrink: true,
                        style: {
                            color: "black",
                        },
                    }}
                    sx={{ mt: 2, mb: 2 }}
                />

                <Box sx={{ height: 400, width: '100%' }}>
                    <DataGrid
                        rows={template.worksheets ?? []}
                        columns={columns}
                        disableSelectionOnClick
                        disableColumnMenu
                        density="standard"
                        hideFooter
                        autoHeight
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#E4EDF0',
                            }
                        }}
                    />
                </Box>
            </Paper>
        );
    }
);

// Add display name for debugging
TemplateWorksheets.displayName = 'TemplateWorksheets';

export default TemplateWorksheets;
